<template>
  <div class="min-h-screen bg-gray-50 pb-16">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
      <!-- Header -->
      <div class="text-center mb-12 animate-fade-in">
        <div class="flex flex-col items-center gap-4">
          <div>
            <h1 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {{ searchQuery ? `Search Results for "${searchQuery}"` : 'Our Products' }}
            </h1>
            <p class="text-xl text-gray-600">
              {{ searchQuery ? `Found ${products.length} product${products.length !== 1 ? 's' : ''}` : 'Discover our premium line of sports supplements' }}
            </p>
          </div>
          <button
            v-if="searchQuery"
            @click="router.push('/products')"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear search
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="productsLoading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <p class="mt-4 text-gray-600">Loading products...</p>
      </div>

      <!-- No Search Results -->
      <div v-else-if="searchQuery && products.length === 0" class="text-center py-12">
        <svg class="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <h3 class="text-2xl font-bold text-gray-900 mb-2">No products found</h3>
        <p class="text-gray-600 mb-6">We couldn't find any products matching "{{ searchQuery }}"</p>
        <button
          @click="router.push('/products')"
          class="px-6 py-3 font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg cursor-pointer"
          style="background-color: #EDD97A; color: #1a1200;"
        >
          View All Products
        </button>
      </div>

      <!-- No products at the moment (catalog empty) -->
      <div v-else-if="products.length === 0" class="text-center py-16 px-4">
        <div class="max-w-md mx-auto">
          <div class="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-200 flex items-center justify-center">
            <svg class="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8 4-8-4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">No products at the moment</h3>
          <p class="text-gray-600 text-sm leading-relaxed mb-6">
            We're currently updating our catalog. New sports supplements will be available soon—please check back later.
          </p>
          <button
            type="button"
            @click="router.push('/')"
            class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 cursor-pointer"
            style="background-color: #EDD97A;"
          >
            Back to Home
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Products Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <!-- Product Card Component (repeated for each product) -->
        <div
          v-for="product in products"
          :key="product.id"
          class="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 group animate-slide-up"
        >
          <!-- Product Image -->
          <div 
            @click="goToProduct(product.id)"
            class="relative bg-gradient-to-br from-gray-50 to-gray-100 p-6 h-64 flex items-center justify-center overflow-hidden cursor-pointer"
          >
            <span 
              class="absolute top-4 left-4 px-3 py-1 text-xs font-bold text-white rounded-full z-10"
              :style="`background: ${product.badgeGradient};`"
            >
              {{ product.badge }}
            </span>
            <img 
              :src="product.image" 
              :alt="product.name"
              class="w-full h-full object-contain transform group-hover:scale-110 transition-transform duration-500"
            >
          </div>
          
          <!-- Product Info -->
          <div class="p-6">
            <h3 class="text-xl font-bold text-gray-900 mb-2">
              {{ product.name }}
            </h3>
            <p class="text-sm text-gray-600 mb-4 line-clamp-4 overflow-hidden">
              {{ product.description }}
            </p>
            
            <!-- Rating -->
            <div class="flex items-center gap-2 mb-4">
              <div class="flex gap-1">
                <svg v-for="star in 5" :key="star" class="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                </svg>
              </div>
              <span class="text-sm text-gray-600">{{ product.rating }} ({{ product.reviews }})</span>
            </div>
            
            <!-- Price -->
            <div class="flex items-center justify-between mb-4">
              <div>
                <p class="text-2xl font-bold text-gray-900">${{ product.price }}</p>
                <p class="text-xs text-green-600">In stock</p>
              </div>
            </div>
            
            <!-- Button -->
            <button 
              @click="addToCart(product.id)" 
              class="w-full px-5 py-3 font-semibold rounded-xl transition-all duration-300 hover:scale-105 shadow-md hover:shadow-lg cursor-pointer"
              style="background-color: #EDD97A; color: #1a1200;"
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useCartStore } from '@/stores/cart'
import { useProducts } from '@/composables/useProducts'
import { mapApiProductToEcommerce, mapApiProductToCartItem } from '@/utils/productMapper'

const router = useRouter()
const route = useRoute()
const cartStore = useCartStore()

// Products from API
const { products: apiProducts, loading: productsLoading, fetchProducts } = useProducts()

// Get search query from route
const searchQuery = computed(() => (route.query.search as string) || '')

// Mapped products for display with search filter
const products = computed(() => {
  const mappedProducts = apiProducts.value.map(mapApiProductToEcommerce)
  
  if (!searchQuery.value) {
    return mappedProducts
  }
  
  const query = searchQuery.value.toLowerCase().trim()
  return mappedProducts.filter(product => {
    const nameMatch = product.name.toLowerCase().includes(query)
    const descriptionMatch = product.description?.toLowerCase().includes(query)
    const categoryMatch = product.category?.toLowerCase().includes(query)
    const skuMatch = product.sku?.toLowerCase().includes(query)
    
    return nameMatch || descriptionMatch || categoryMatch || skuMatch
  })
})

const addToCart = (productId: string) => {
  const apiProduct = apiProducts.value.find(p => p.Product_id.toString() === productId)
  if (apiProduct) {
    const cartItem = mapApiProductToCartItem(apiProduct)
    cartStore.addItem(cartItem)
  }
}

const goToProduct = (productId: string) => {
  router.push(`/product/${productId}`)
}

onMounted(async () => {
  await fetchProducts()
})
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.8s ease-out;
  animation-fill-mode: both;
}

.animate-slide-up:nth-child(1) { animation-delay: 0.1s; }
.animate-slide-up:nth-child(2) { animation-delay: 0.15s; }
.animate-slide-up:nth-child(3) { animation-delay: 0.2s; }
.animate-slide-up:nth-child(4) { animation-delay: 0.25s; }
.animate-slide-up:nth-child(5) { animation-delay: 0.3s; }
.animate-slide-up:nth-child(6) { animation-delay: 0.35s; }
.animate-slide-up:nth-child(7) { animation-delay: 0.4s; }
.animate-slide-up:nth-child(8) { animation-delay: 0.45s; }
.animate-slide-up:nth-child(9) { animation-delay: 0.5s; }
.animate-slide-up:nth-child(10) { animation-delay: 0.55s; }
.animate-slide-up:nth-child(11) { animation-delay: 0.6s; }
.animate-slide-up:nth-child(12) { animation-delay: 0.65s; }
</style>

